import { useEffect, useRef, useState } from "react";

interface Props {
  onComplete: () => void;
}

// Progress easing — never visually stalls
function easedProgress(ms: number): number {
  const p1 = 900,  t1 = 55;  // 0–900ms   → 0–55%
  const p2 = 1700, t2 = 82;  // 900–1700  → 55–82%
  const p3 = 2300, t3 = 96;  // 1700–2300 → 82–96%
  const p4 = 2600;            // 2300–2600 → 96–100%

  if (ms <= p1) {
    const t = ms / p1;
    return t1 * (1 - Math.pow(1 - t, 2));
  } else if (ms <= p2) {
    const t = (ms - p1) / (p2 - p1);
    return t1 + (t2 - t1) * (1 - Math.pow(1 - t, 1.6));
  } else if (ms <= p3) {
    const t = (ms - p2) / (p3 - p2);
    return t2 + (t3 - t2) * (1 - Math.pow(1 - t, 1.3));
  } else if (ms <= p4) {
    return t3 + (100 - t3) * ((ms - p3) / (p4 - p3));
  }
  return 100;
}

// Each transaction particle: which SVG path id, delay (s), direction label
const PARTICLES: { path: string; begin: number; isIn: boolean }[] = [
  // Incoming (top → wallet)
  { path: "pIn1",  begin: 0.00, isIn: true },
  { path: "pIn1",  begin: 1.10, isIn: true },
  { path: "pIn2",  begin: 0.40, isIn: true },
  { path: "pIn2",  begin: 1.50, isIn: true },
  { path: "pIn3",  begin: 0.80, isIn: true },
  { path: "pIn3",  begin: 1.90, isIn: true },
  // Outgoing (wallet → bottom)
  { path: "pOut1", begin: 0.60, isIn: false },
  { path: "pOut1", begin: 1.70, isIn: false },
  { path: "pOut2", begin: 1.00, isIn: false },
  { path: "pOut2", begin: 2.10, isIn: false },
  { path: "pOut3", begin: 1.40, isIn: false },
  { path: "pOut3", begin: 2.50, isIn: false },
];

export default function PreLoader({ onComplete }: Props) {
  const [progress, setProgress] = useState(0);
  const [balance,  setBalance]  = useState(0);
  const [phase,    setPhase]    = useState<"enter" | "exit">("enter");
  const rafRef  = useRef<number>(0);
  const doneRef = useRef(false);

  // ── Progress counter ──
  useEffect(() => {
    const HOLD = 380, EXIT = 500;
    const start = performance.now();

    const finish = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      setProgress(100);
      setTimeout(() => { setPhase("exit"); setTimeout(onComplete, EXIT); }, HOLD);
    };

    const tick = (now: number) => {
      const pct = Math.min(Math.floor(easedProgress(now - start)), 100);
      setProgress(pct);
      pct < 100 ? (rafRef.current = requestAnimationFrame(tick)) : finish();
    };
    rafRef.current = requestAnimationFrame(tick);

    // Hard safety cap — never stuck
    const safe = setTimeout(finish, 5500);
    return () => { cancelAnimationFrame(rafRef.current); clearTimeout(safe); };
  }, [onComplete]);

  // ── Balance counter (animates alongside progress) ──
  useEffect(() => {
    const TARGET = 3428.5, DUR = 2500;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / DUR, 1);
      setBalance(TARGET * (1 - Math.pow(1 - t, 3)));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, []);

  const isExiting = phase === "exit";

  return (
    <div
      aria-label="Loading CashTap"
      aria-live="polite"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center select-none overflow-hidden"
      style={{
        background: "#090D14",
        opacity:   isExiting ? 0 : 1,
        transform: isExiting ? "scale(1.04)" : "scale(1)",
        transition: isExiting ? "opacity 0.5s ease-in, transform 0.5s ease-in" : "none",
        pointerEvents: isExiting ? "none" : "all",
      }}
    >

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          BG LAYER 1 — Rotating beam
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute"
          style={{
            top: "50%", left: "50%",
            width: "220vmax", height: "220vmax",
            transform: "translate(-50%, -50%)",
            background: "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(97,206,112,0.04) 25deg, transparent 50deg, transparent 360deg)",
            animation: "bgBeamRotate 16s linear infinite",
          }}
        />
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          BG LAYER 2 — Scrolling dot grid
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(97,206,112,0.07) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          animation: "dotScroll 5s linear infinite",
        }}
      />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          BG LAYER 3 — Ambient glows
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div style={{
          position: "absolute", top: "-20%", left: "10%",
          width: "500px", height: "400px", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(97,206,112,0.09) 0%, transparent 65%)",
          filter: "blur(60px)",
          animation: "glowDrift1 10s ease-in-out infinite",
        }}/>
        <div style={{
          position: "absolute", bottom: "-15%", right: "5%",
          width: "420px", height: "380px", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(60,170,80,0.07) 0%, transparent 65%)",
          filter: "blur(70px)",
          animation: "glowDrift2 13s ease-in-out infinite",
        }}/>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          MAIN SCENE — SVG wallet animation
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div
        className="relative z-10 w-full flex flex-col items-center"
        style={{ animation: "stageEnter 0.8s cubic-bezier(0.22,1,0.36,1) forwards" }}
      >
        <svg
          viewBox="0 0 480 300"
          className="w-full"
          style={{ maxWidth: "480px", maxHeight: "300px" }}
          aria-hidden="true"
        >
          <defs>
            {/* Card gradient */}
            <linearGradient id="cardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#0f2016"/>
              <stop offset="100%" stopColor="#091209"/>
            </linearGradient>

            {/* Particle glow filter */}
            <filter id="pglow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="2.5" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>

            {/* Card inner glow */}
            <filter id="cardGlow" x="-10%" y="-10%" width="120%" height="120%">
              <feGaussianBlur stdDeviation="4" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>

            {/* ── Transaction arc paths ── */}
            {/* Incoming: node → card */}
            <path id="pIn1"  d="M 38,32  Q 88,60  148,93"/>
            <path id="pIn2"  d="M 240,12 Q 240,48 240,93"/>
            <path id="pIn3"  d="M 442,32 Q 392,60 332,93"/>
            {/* Outgoing: card → node */}
            <path id="pOut1" d="M 148,207 Q 88,240  38,268"/>
            <path id="pOut2" d="M 240,207 Q 240,248 240,288"/>
            <path id="pOut3" d="M 332,207 Q 392,240 442,268"/>
          </defs>

          {/* ── Faint arc track lines ── */}
          {["pIn1","pIn2","pIn3","pOut1","pOut2","pOut3"].map(id => (
            <use key={id} href={`#${id}`}
              stroke="rgba(97,206,112,0.12)" strokeWidth="1" fill="none"
              strokeDasharray="4 4"
            />
          ))}

          {/* ── Source / destination node circles ── */}
          {[
            [38,32],[240,12],[442,32],   // incoming sources
            [38,268],[240,288],[442,268], // outgoing destinations
          ].map(([x,y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r="7" fill="rgba(97,206,112,0.08)" stroke="rgba(97,206,112,0.25)" strokeWidth="1"/>
              <circle cx={x} cy={y} r="3" fill="#61CE70" opacity="0.7">
                <animate attributeName="opacity" values="0.4;1;0.4" dur={`${2.2 + i * 0.3}s`} repeatCount="indefinite"/>
              </circle>
            </g>
          ))}

    

          {/* ── Animated particles along arcs ── */}
          {PARTICLES.map((p, i) => (
            <circle
              key={i}
              r={p.isIn ? 3.5 : 3}
              fill={p.isIn ? "#61CE70" : "rgba(97,206,112,0.75)"}
              filter="url(#pglow)"
            >
              <animateMotion
                dur="2.2s"
                repeatCount="indefinite"
                begin={`${p.begin}s`}
              >
                <mpath href={`#${p.path}`}/>
              </animateMotion>
              <animate
                attributeName="opacity"
                values="0;0.9;1;0.9;0"
                keyTimes="0;0.1;0.5;0.9;1"
                dur="2.2s"
                repeatCount="indefinite"
                begin={`${p.begin}s`}
              />
            </circle>
          ))}

          {/* ════════════════════════════════
              WALLET CARD — hero element
          ════════════════════════════════ */}
          <g style={{ filter: "drop-shadow(0 0 14px rgba(97,206,112,0.15))" }}>
            {/* Card body */}
            <rect x="145" y="93" width="190" height="116" rx="13"
              fill="url(#cardGrad)"
              stroke="rgba(97,206,112,0.28)" strokeWidth="1"
            />

            {/* Card inner highlight (top edge) */}
            <rect x="145" y="93" width="190" height="3" rx="13"
              fill="rgba(97,206,112,0.08)"
            />

            {/* EMV Chip */}
            <rect x="162" y="111" width="24" height="18" rx="3"
              fill="none" stroke="rgba(97,206,112,0.45)" strokeWidth="0.8"
            />
            <line x1="174" y1="111" x2="174" y2="129" stroke="rgba(97,206,112,0.35)" strokeWidth="0.7"/>
            <line x1="162" y1="120" x2="186" y2="120" stroke="rgba(97,206,112,0.35)" strokeWidth="0.7"/>

            {/* Logo */}
            <image href="/logo.png" x="308" y="106" width="22" height="22"
              style={{ objectFit: "contain" }}
            />

            {/* Contactless icon (3 arcs) */}
            <g transform="translate(296,108)" fill="none" stroke="rgba(97,206,112,0.4)" strokeWidth="0.9" strokeLinecap="round">
              <path d="M 3,5 A 6,6 0 0 1 3,-5"/>
              <path d="M 6,8 A 11,11 0 0 1 6,-8"/>
              <path d="M 0,2.5 A 2.5,2.5 0 0 1 0,-2.5"/>
            </g>

            {/* Balance — animated counter */}
            <text x="240" y="160" textAnchor="middle"
              fill="white" fontSize="19" fontWeight="700"
              fontFamily="'JetBrains Mono', 'Fira Mono', monospace"
              letterSpacing="-0.5"
            >
              ${balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </text>

            {/* Currency label */}
            <text x="240" y="173" textAnchor="middle"
              fill="rgba(97,206,112,0.65)" fontSize="8" fontWeight="600"
              fontFamily="sans-serif" letterSpacing="2.5"
            >
              Cashtap · Wallet
            </text>

            {/* Card number dots */}
            <text x="165" y="197" fill="rgba(255,255,255,0.22)" fontSize="10" letterSpacing="2.5"
              fontFamily="monospace"
            >
              ••••  ••••  ••••  ••••
            </text>

            {/* Animated pulse border on card */}
            <rect x="145" y="93" width="190" height="116" rx="13"
              fill="none" stroke="#61CE70" strokeWidth="0.8" opacity="0"
            >
              <animate attributeName="opacity" values="0;0.35;0" dur="2.8s" repeatCount="indefinite"/>
              <animate attributeName="stroke-width" values="0.8;2;0.8" dur="2.8s" repeatCount="indefinite"/>
            </rect>
          </g>

          {/* ── Arrow direction indicators on arcs ── */}
          {/* Small chevrons near the wallet entry points */}
          <g fill="none" stroke="rgba(97,206,112,0.35)" strokeWidth="1.2" strokeLinecap="round">
            {/* Incoming arrow hints */}
            <polyline points="162,106 157,100 162,94"/>
            <polyline points="234,105 240,99 246,105"/>
            <polyline points="318,106 323,100 318,94"/>
            {/* Outgoing arrow hints */}
            <polyline points="162,194 157,200 162,206"/>
            <polyline points="234,195 240,201 246,195"/>
            <polyline points="318,194 323,200 318,206"/>
          </g>
        </svg>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            BRAND + PROGRESS
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div
          className="flex flex-col items-center gap-4 mt-2"
          style={{ animation: "fadeUp 0.7s ease-out 0.35s both" }}
        >
          {/* Brand */}
          <div className="flex flex-col items-center gap-1">
            <h1 className="text-2xl font-bold text-white tracking-tight"
              style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", letterSpacing: "-0.02em" }}>
              CashTap
            </h1>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em]"
              style={{ color: "rgba(97,206,112,0.55)" }}>
              Secure · Fast · Decentralized
            </p>
          </div>

          {/* Progress bar */}
          <div className="flex flex-col items-center gap-2" style={{ width: "176px" }}>
            <div className="w-full rounded-full overflow-hidden relative"
              style={{ height: "2px", background: "rgba(255,255,255,0.07)" }}>
              <div style={{
                height: "100%",
                width: `${progress}%`,
                background: "linear-gradient(90deg, #3da84e, #61CE70)",
                borderRadius: "9999px",
                boxShadow: "0 0 10px rgba(97,206,112,0.6)",
                transition: "width 0.1s linear",
                position: "relative",
              }}>
                {/* Shimmer on bar */}
                <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                  animation: "barShimmer 1.8s ease-in-out infinite",
                }}/>
              </div>
            </div>

            <p className="text-[12px] font-semibold tabular-nums"
              style={{
                fontFamily: "'JetBrains Mono','Fira Mono',monospace",
                color: progress === 100 ? "#61CE70" : "rgba(255,255,255,0.35)",
                transition: "color 0.4s ease",
                letterSpacing: "0.06em",
              }}>
              {String(progress).padStart(3, "\u2007")}%
            </p>
          </div>
        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          KEYFRAMES
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <style>{`
        @keyframes bgBeamRotate {
          from { transform: translate(-50%,-50%) rotate(0deg); }
          to   { transform: translate(-50%,-50%) rotate(360deg); }
        }
        @keyframes dotScroll {
          from { background-position: 0 0; }
          to   { background-position: 30px 30px; }
        }
        @keyframes glowDrift1 {
          0%,100% { transform: translate(0,0) scale(1); }
          40%      { transform: translate(40px,-50px) scale(1.08); }
          70%      { transform: translate(70px,30px) scale(0.95); }
        }
        @keyframes glowDrift2 {
          0%,100% { transform: translate(0,0) scale(1); }
          35%      { transform: translate(-50px,40px) scale(1.05); }
          65%      { transform: translate(-30px,-40px) scale(0.94); }
        }
        @keyframes stageEnter {
          from { opacity:0; transform: scale(0.9) translateY(18px); }
          to   { opacity:1; transform: scale(1)   translateY(0); }
        }
        @keyframes fadeUp {
          from { opacity:0; transform: translateY(10px); }
          to   { opacity:1; transform: translateY(0); }
        }
        @keyframes barShimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration:0.01ms !important;
            transition-duration:0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
}
